import { useState, useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack5';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import moment from 'moment'; // Ensure moment is imported
import { MIconButton } from '../components/@material-extend';

export const useFileDownload = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const downloadControllerRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadFile = useCallback(async ({
        apiFunction,
        apiParams = {},
        baseFileName = 'Export', // The main name (e.g., 'Supervisor Report')
        extension = 'xlsx',       // The file extension
        successMsg = 'Downloaded successfully',
        errorMsg = 'Failed to download file. Please try again.',
        loadingMsg = 'Downloading file, please wait...',
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }) => {
        // --- FILENAME GENERATION ---
        const currentDateTime = moment().format('MMMM Do YYYY, h:mm:ss a');
        const finalFileName = `${baseFileName} - ${currentDateTime}.${extension}`;

        if (downloadControllerRef.current) {
            downloadControllerRef.current.abort();
        }

        const controller = new AbortController();
        downloadControllerRef.current = controller;
        const { signal } = controller;

        const snackbarKey = enqueueSnackbar(loadingMsg, {
            variant: "info",
            persist: true,
            action: (key) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                    <Icon icon={closeFill} />
                </MIconButton>
            )
        });

        try {
            setIsDownloading(true);
            const { data } = await apiFunction(apiParams, signal);

            const url = URL.createObjectURL(new Blob([data], { type: mimeType }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', finalFileName); // Using the generated name
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);

            closeSnackbar(snackbarKey);
            enqueueSnackbar(successMsg, {
                variant: "success",
                autoHideDuration: 3000,
                action: (key) => (
                    <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </MIconButton>
                )
            });
        } catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                closeSnackbar(snackbarKey);
                return;
            }
            console.error("Download error:", err);
            closeSnackbar(snackbarKey);
            enqueueSnackbar(errorMsg, {
                variant: "error", autoHideDuration: 3000, action: (key) => (
                    <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </MIconButton>
                )
            });
            setIsDownloading(false);
            downloadControllerRef.current = null
        } finally {
            if (downloadControllerRef.current === controller) {
                setIsDownloading(false);
                downloadControllerRef.current = null;
            }
        }
    }, [enqueueSnackbar, closeSnackbar]);

    return { downloadFile, isDownloading };
};